
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Infinity } from 'lucide-react';

export type ChatMode = 'plan' | 'execute';

interface AgentModeDropdownProps {
  mode: ChatMode;
  onChange: (mode: ChatMode) => void;
  disabled?: boolean;
}

const modeConfig = {
  plan: {
    label: 'Plan',
    description: 'Analyze and plan approach',
  },
  execute: {
    label: 'Execute', 
    description: 'Write and modify code',
  },
};

export function AgentModeDropdown({
  mode,
  onChange,
  disabled = false,
}: AgentModeDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (newMode: ChatMode) => {
    onChange(newMode);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex items-center gap-1 px-2 py-1 rounded-md w-[90px] justify-between
          bg-muted hover:bg-accent text-foreground
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          transition-all duration-200 text-xs font-medium
        `}
      >
        <div className="flex items-center gap-1">
          <Infinity className="w-3 h-3" />
          <span>{modeConfig[mode].label}</span>
        </div>
        <ChevronDown className={`w-2.5 h-2.5 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-0 mb-1 w-48 bg-popover rounded-lg shadow-lg border border-border z-50">
          <div className="p-1">
            {Object.entries(modeConfig).map(([key, config]) => {
              const isSelected = mode === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleSelect(key as ChatMode)}
                  className={`
                    w-full flex items-center justify-between px-3 py-2 rounded-md
                    ${isSelected ? 'bg-accent' : 'hover:bg-accent'}
                    transition-colors duration-150
                  `}
                >
                  <span className="text-sm text-foreground">{config.label}</span>
                  {isSelected && (
                    <div className="w-2 h-2 rounded-full bg-primary" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}